import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { financialRecordService } from "../services/service/financialRecordService";

const EMPTY_PARAMS = {};
const FINANCIAL_RECORDS_KEY = ["financial-records"];

export const useShopFinancialRecords = (
    shopId,
    initialParams = EMPTY_PARAMS
) => {
    const queryClient = useQueryClient();

    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        search: searchTerm || undefined,
        category:
            categoryFilter !== "all"
                ? categoryFilter
                : undefined,
        status:
            statusFilter !== "all"
                ? statusFilter
                : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [
        currentPage,
        pageSize,
        searchTerm,
        categoryFilter,
        statusFilter,
        startDate,
        endDate,
        initialParams
    ]);

    const query = useQuery({
        queryKey: [
            ...FINANCIAL_RECORDS_KEY,
            "shop",
            shopId,
            params
        ],
        queryFn: () =>
            financialRecordService.getAllByShop(
                shopId,
                params
            ),
        enabled: Boolean(shopId),
        staleTime: Infinity,
    });

    const totalQuery = useQuery({
        queryKey: [
            ...FINANCIAL_RECORDS_KEY,
            "shop-total",
            shopId
        ],
        queryFn: () =>
            financialRecordService.getTotalByShop(shopId),
        enabled: Boolean(shopId),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() => {
        return queryClient.invalidateQueries({
            queryKey: FINANCIAL_RECORDS_KEY
        });
    }, [queryClient]);

    const createFinancialRecord = useCallback(
        async (data) => {
            const response =
                await financialRecordService.create(data);

            await invalidate();

            return response;
        },
        [invalidate]
    );

    const updateFinancialRecord = useCallback(
        async (financialId, data) => {
            const response =
                await financialRecordService.update(
                    financialId,
                    data
                );

            await invalidate();

            return response;
        },
        [invalidate]
    );

    const toggleFinancialRecordStatus = useCallback(
        async (financialId) => {
            const response =
                await financialRecordService.toggleStatus(
                    financialId
                );

            await invalidate();

            return response;
        },
        [invalidate]
    );

    return {
        financialRecords: query.data?.data?.data || [],
        pagination: query.data?.data || null,

        totals: totalQuery.data?.data || null,

        loading:
            query.isLoading ||
            query.isFetching ||
            totalQuery.isLoading ||
            totalQuery.isFetching,

        error: query.error || totalQuery.error,

        searchTerm,
        setSearchTerm,

        categoryFilter,
        setCategoryFilter,

        statusFilter,
        setStatusFilter,

        currentPage,
        setCurrentPage,

        pageSize,
        setPageSize,

        startDate,
        setStartDate,

        endDate,
        setEndDate,

        refresh: query.refetch,
        refreshTotals: totalQuery.refetch,

        createFinancialRecord,
        updateFinancialRecord,
        toggleFinancialRecordStatus,
    };
};

export const useFinancialRecords = useShopFinancialRecords;
export const useFinancialRecord = useShopFinancialRecords;